import { ApplicationTypeEnum } from '../screening-questions/ScreeningQuestionsInterfaces';

export enum EvaluationCriterionTypeEnum {
    RATING = 'rating',
    YES_NO = 'yes_no',
    SINGLE_SELECT = 'single_select',
    MULTI_SELECT = 'multi_select',
    SHORT_TEXT = 'short_text',
    LONG_TEXT = 'long_text',
    SECTION = 'section',
}

export enum EvaluationVerdictEnum {
    STRONG_YES = 'strong_yes',
    YES = 'yes',
    MAYBE = 'maybe',
    NO = 'no',
}

export enum EvaluationTemplateStatusEnum {
    ACTIVE = 'active',
    ARCHIVED = 'archived',
}

export interface EvaluationCriterion {
    key: string;
    type: EvaluationCriterionTypeEnum;
    label: string;
    helper?: string;
    required?: boolean;
    options?: string[];
}

export type EvaluationResponses = Record<string, string | number | string[]>;

export interface EvaluationAttachedJob {
    id: string;
    title?: string;
}

export interface EvaluationTemplateListItem {
    id: string;
    name: string;
    description?: string;
    status: EvaluationTemplateStatusEnum;
    isSeeded: boolean;
    currentVersion: number;
    criteriaCount: number;
    attachedJobs: EvaluationAttachedJob[];
    updatedAt: string | Date;
}

export interface EvaluationTemplateDetail {
    id: string;
    name: string;
    description?: string;
    status: EvaluationTemplateStatusEnum;
    isSeeded: boolean;
    currentVersion: number;
    criteria: EvaluationCriterion[];
    templateVersionId?: string;
    updatedAt: string | Date;
    versionBumped?: boolean;
}

export interface CreateEvaluationTemplateRequest {
    name: string;
    description?: string;
    criteria: EvaluationCriterion[];
}

export interface UpdateEvaluationTemplateRequest extends CreateEvaluationTemplateRequest {}

export interface JobEvaluationAttachment {
    templateId: string;
    templateName: string;
    stageId: string;
    stageName?: { ar?: string; en?: string } | string;
    currentVersion: number;
    attachedAt: string | Date;
}

export interface UpsertJobEvaluationAttachmentRequest {
    templateId: string;
    stageId: string;
}

export interface InterviewEvaluationEvaluator {
    id: string;
    name: string;
}

export interface InterviewEvaluationTemplateVersion {
    id: string;
    number: number;
    name: string;
    isStale: boolean;
}

export interface InterviewEvaluationItem {
    id: string;
    verdict: EvaluationVerdictEnum;
    responses: EvaluationResponses;
    criteria: EvaluationCriterion[];
    submittedAt: string | Date;
    editedAt?: string | Date;
    evaluator: InterviewEvaluationEvaluator;
    templateVersion: InterviewEvaluationTemplateVersion;
}

export interface InterviewEvaluationSummary {
    latestVerdict?: EvaluationVerdictEnum | null;
    evaluationsCount: number;
    distinctEvaluators: number;
    myLockedEvaluationId?: string | null;
}

export interface SubmitEvaluationRequest {
    templateVersionId: string;
    verdict: EvaluationVerdictEnum;
    responses: EvaluationResponses;
}

export interface UpdateEvaluationRequest {
    verdict: EvaluationVerdictEnum;
    responses: EvaluationResponses;
}

export interface EvaluationSummariesBatchRequest {
    applicationIds: Array<{
        applicationExternalId: string;
        applicationType: ApplicationTypeEnum;
    }>;
}

export interface InterviewEvaluationsStoreInterface {
    templates: EvaluationTemplateListItem[];
    selectedTemplate: EvaluationTemplateDetail | null;
    isLoadingTemplates: boolean;
    isLoadingTemplateDetail: boolean;
    isSavingTemplate: boolean;

    jobAttachment: JobEvaluationAttachment | null;
    isLoadingJobAttachment: boolean;
    isSavingJobAttachment: boolean;
    /** Bumped on attachment write so in-flight GETs can be ignored as stale. */
    attachmentEpoch: number;

    evaluations: InterviewEvaluationItem[];
    evaluationSummary: InterviewEvaluationSummary | null;
    isLoadingEvaluations: boolean;
    isSubmittingEvaluation: boolean;
    isDeletingEvaluation: boolean;

    showSubmitPanel: boolean;
    editingEvaluationId: string | null;
}

export const MAX_EVALUATION_CRITERIA = 30;
export const EVALUATION_EDIT_WINDOW_HOURS = 24;
export const SHORT_TEXT_MAX = 300;
export const LONG_TEXT_MAX = 5000;
